import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { FORWARDING_CHECK_STATE, type ForwardingCheck } from "./types";

const STATE_MESSAGE: Record<ForwardingCheck["state"], string> = {
  [FORWARDING_CHECK_STATE.unsent]:
    "Set the rule up, then send the test. We will tell you when the message reaches us.",
  [FORWARDING_CHECK_STATE.waiting]:
    "Test sent. It usually returns in less than a minute. Keep this page open to see the result.",
  [FORWARDING_CHECK_STATE.confirmed]:
    "The test message reached Volca. Check that your vendor invoice rule is also enabled.",
  [FORWARDING_CHECK_STATE.timedOut]:
    "Nothing reached us. The usual causes are a rule that is saved but switched off, a rule pointing at the wrong address, or a typo in your own address above.",
};

export const ForwardingTestResult = ({ check }: { check: ForwardingCheck }) => {
  const confirmed = check.state === FORWARDING_CHECK_STATE.confirmed;
  return (
    <Card className="w-full gap-0 py-0">
      <CardContent className="p-6">
        <Stack className="w-full" gap={4} align="stretch">
          <Text variant="title">3. Check the result</Text>

          <Text tone="muted">
            After you send the test, we check that your test rule forwards the
            email from your current AP inbox to Volca. The result appears here.
          </Text>

          <Stack
            className="w-full"
            direction="row"
            gap={2}
            align="start"
            role="status"
          >
            {confirmed && (
              <CheckCircleIcon
                weight="fill"
                className="h-6 w-6 shrink-0 text-success"
                aria-hidden="true"
              />
            )}
            <Stack className="w-full" align="start" gap={1}>
              {confirmed && (
                <Text variant="title" tone="success">
                  Test successful
                </Text>
              )}
              <Text tone="muted">{STATE_MESSAGE[check.state]}</Text>
            </Stack>
          </Stack>

          {!confirmed && (
            <Text tone="muted">
              If the test does not arrive, check your invoice email address, the
              destination address, and that the test rule is enabled. Ask your
              email administrator to check external forwarding restrictions.
              Correct the problem and send another test. If it still does not
              arrive, contact Volca support. You can continue and return to the
              forwarding instructions later.
            </Text>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
